import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';

import { apiGet } from '../ApiClient';
import config from '../config';

function getSocketOrigin() {
  if (typeof window === 'undefined') return '';
  const socketOriginUrl = new URL(config.apiBaseUrl || '/', window.location.origin);
  socketOriginUrl.protocol = socketOriginUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  return socketOriginUrl.origin;
}

function shouldRefreshItems(data) {
  return data?.doctype === 'POS Invoice' || data?.doctype === 'Bin';
}

/**
 * Subscribe to Frappe realtime doctype updates that can change POS stock.
 * POS Invoice submission changes stock through ledger/bin updates, while Bin
 * updates also catch stock changes made outside this POS terminal.
 */
export default function usePosRealtimeRefetch(enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    let socket;
    let cancelled = false;

    async function connect() {
      try {
        const res = await apiGet('method/custom_api.api.currentSite');
        const siteName = res?.message;
        if (!siteName || cancelled) return;

        socket = io(`${getSocketOrigin()}/${siteName}`, {
          transports: ['websocket'],
          withCredentials: true,
          reconnectionAttempts: 3,
        });

        socket.on('connect', () => {
          socket.emit('doctype_subscribe', 'POS Invoice');
          socket.emit('doctype_subscribe', 'Bin');
        });

        socket.on('list_update', (data) => {
          if (shouldRefreshItems(data)) {
            queryClient.invalidateQueries({ queryKey: ['posItems'] });
          }
        });

        socket.on('doc_update', (data) => {
          if (shouldRefreshItems(data)) {
            queryClient.invalidateQueries({ queryKey: ['posItems'] });
          }
        });
      } catch {
        // Realtime is best-effort; normal API refetches still keep the POS usable.
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (socket) {
        socket.emit('doctype_unsubscribe', 'POS Invoice');
        socket.emit('doctype_unsubscribe', 'Bin');
        socket.disconnect();
      }
    };
  }, [enabled, queryClient]);
}
